import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Check, ExternalLink, Calendar, Star, Megaphone, Target, Mail, Gift, Sparkles, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function Subscription() {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [featuredCancelLoading, setFeaturedCancelLoading] = useState(false);
  const [featuredResumeLoading, setFeaturedResumeLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // monthly | annual
  const [marketingLoading, setMarketingLoading] = useState(false);
  const [marketingCancelLoading, setMarketingCancelLoading] = useState(false);
  const [marketingResumeLoading, setMarketingResumeLoading] = useState(false);
  const [plusLoading, setPlusLoading] = useState(false);
  const [plusCancelLoading, setPlusCancelLoading] = useState(false);
  const [plusResumeLoading, setPlusResumeLoading] = useState(false);

  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line

  const active = user?.subscription_active;
  const status = user?.subscription_status;
  const periodEnd = user?.subscription_current_period_end;
  const cancelAtEnd = user?.cancel_at_period_end;
  const featured = user?.featured_active;
  const featuredPeriodEnd = user?.featured_current_period_end;
  const featuredCancelAtEnd = user?.featured_cancel_at_period_end;
  const marketing = user?.marketing_active;
  const marketingPeriodEnd = user?.marketing_current_period_end;
  const marketingCancelAtEnd = user?.marketing_cancel_at_period_end;
  const plus = user?.marketing_plus_active;
  const plusPeriodEnd = user?.marketing_plus_current_period_end;
  const plusCancelAtEnd = user?.marketing_plus_cancel_at_period_end;

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/checkout", {
        origin_url: window.location.origin,
        plan: billingPeriod,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/subscription/portal", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setPortalLoading(false);
    }
  };

  const cancel = async () => {
    setCancelLoading(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription will end at period close");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCancelLoading(false);
    }
  };

  const resume = async () => {
    setResumeLoading(true);
    try {
      await api.post("/subscription/resume");
      toast.success("Subscription resumed");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setResumeLoading(false);
    }
  };

  const featuredCheckout = async () => {
    setFeaturedLoading(true);
    try {
      const { data } = await api.post("/subscription/featured/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setFeaturedLoading(false);
    }
  };

  const featuredCancel = async () => {
    setFeaturedCancelLoading(true);
    try {
      await api.post("/subscription/featured/cancel");
      toast.success("Featured Listing will end at period close");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setFeaturedCancelLoading(false);
    }
  };

  const featuredResume = async () => {
    setFeaturedResumeLoading(true);
    try {
      await api.post("/subscription/featured/resume");
      toast.success("Featured Listing resumed");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setFeaturedResumeLoading(false);
    }
  };

  const marketingCheckout = async () => {
    setMarketingLoading(true);
    try {
      const { data } = await api.post("/subscription/marketing/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setMarketingLoading(false);
    }
  };

  const marketingCancel = async () => {
    setMarketingCancelLoading(true);
    try {
      await api.post("/subscription/marketing/cancel");
      toast.success("Marketing add-on will end at period close");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setMarketingCancelLoading(false);
    }
  };

  const marketingResume = async () => {
    setMarketingResumeLoading(true);
    try {
      await api.post("/subscription/marketing/resume");
      toast.success("Marketing add-on resumed");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setMarketingResumeLoading(false);
    }
  };

  const plusCheckout = async () => {
    setPlusLoading(true);
    try {
      const { data } = await api.post("/subscription/marketing-plus/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setPlusLoading(false);
    }
  };

  const plusCancel = async () => {
    setPlusCancelLoading(true);
    try {
      await api.post("/subscription/marketing-plus/cancel");
      toast.success("Marketing Plus will end at period close");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setPlusCancelLoading(false);
    }
  };

  const plusResume = async () => {
    setPlusResumeLoading(true);
    try {
      await api.post("/subscription/marketing-plus/resume");
      toast.success("Marketing Plus resumed");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setPlusResumeLoading(false);
    }
  };

  const statusBadge = () => {
    if (!status) return <Badge variant="outline" className="rounded-sm">Free</Badge>;
    if (cancelAtEnd) return <Badge className="rounded-sm bg-amber-500 hover:bg-amber-500">Cancels at period end</Badge>;
    if (status === "active") return <Badge className="rounded-sm bg-emerald-500 hover:bg-emerald-500">Active</Badge>;
    if (status === "trialing") return <Badge className="rounded-sm bg-blue-500 hover:bg-blue-500">Trialing</Badge>;
    if (status === "past_due") return <Badge variant="destructive" className="rounded-sm">Past due</Badge>;
    return <Badge variant="outline" className="rounded-sm">{status}</Badge>;
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
      <div className="overline text-gray-500">Business subscription</div>
      <h1 className="font-display text-5xl tracking-tighter mt-2">Unlock the customer ledger</h1>
      <p className="text-gray-600 mt-3 max-w-xl">Your business listing stays free, forever. The monthly subscription unlocks two things only: <strong>posting reviews on customers</strong> and <strong>reading customer reviews</strong>. Cancel anytime in Stripe's secure billing portal.</p>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="rounded-sm border border-border p-8">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="rounded-sm">Current plan</Badge>
            {statusBadge()}
          </div>
          <h3 className="font-display text-3xl mt-4" data-testid="subscription-plan-name">{active || status ? "servrevu Pro" : "Free"}</h3>

          {periodEnd && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-600" data-testid="subscription-period-end">
              <Calendar size={14} />
              {cancelAtEnd ? "Access until " : "Renews on "}
              {new Date(periodEnd).toLocaleDateString()}
            </div>
          )}

          {active && (
            <div className="mt-6 flex flex-col gap-2">
              <Button
                variant="outline"
                className="rounded-[35px] justify-between"
                onClick={openPortal}
                disabled={portalLoading}
                data-testid="subscription-portal-button"
              >
                {portalLoading ? "Opening..." : "Manage billing in Stripe"}
                <ExternalLink size={14} />
              </Button>

              {cancelAtEnd ? (
                <Button
                  className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]"
                  onClick={resume}
                  disabled={resumeLoading}
                  data-testid="subscription-resume-button"
                >
                  {resumeLoading ? "Resuming..." : "Resume subscription"}
                </Button>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" className="rounded-[35px] shadow-sm hover:shadow-md text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="subscription-cancel-button">
                      Cancel subscription
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-sm">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel subscription?</AlertDialogTitle>
                      <AlertDialogDescription>
                        You'll keep access until {periodEnd ? new Date(periodEnd).toLocaleDateString() : "the end of the current period"}. After that, customer reviews will be locked again.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-sm" data-testid="subscription-cancel-dismiss">Keep subscription</AlertDialogCancel>
                      <AlertDialogAction onClick={cancel} disabled={cancelLoading} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="subscription-cancel-confirm">
                        {cancelLoading ? "Cancelling..." : "Yes, cancel"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          )}
        </Card>

        <Card className={`rounded-sm border p-8 ${active ? "border-border bg-white" : "border-[#B31E1E] bg-[#B31E1E] text-white"}`}>
          <Badge className={`rounded-sm ${active ? "" : "bg-white/20 hover:bg-white/20 text-white border-0"}`} variant={active ? "outline" : undefined}>
            {active ? "What you get" : "Recommended"}
          </Badge>
          <h3 className="font-display text-3xl mt-4">servrevu Pro</h3>

          {!active && (
            <div className={`mt-4 inline-flex rounded-md p-0.5 bg-white/15`} data-testid="billing-period-toggle">
              <button
                type="button"
                onClick={() => setBillingPeriod("monthly")}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  billingPeriod === "monthly" ? "bg-white text-[#B31E1E]" : "text-white/85 hover:text-white"
                }`}
                data-testid="billing-period-monthly"
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingPeriod("annual")}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${
                  billingPeriod === "annual" ? "bg-white text-[#B31E1E]" : "text-white/85 hover:text-white"
                }`}
                data-testid="billing-period-annual"
              >
                Annual
                <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-bold tracking-wide uppercase ${
                  billingPeriod === "annual" ? "bg-amber-400 text-black" : "bg-amber-400/90 text-black"
                }`}>
                  2 months free
                </span>
              </button>
            </div>
          )}

          {billingPeriod === "annual" && !active ? (
            <>
              <p className="mt-4 text-3xl font-display">
                $99.50<span className="text-base opacity-70">/year</span>
              </p>
              <p className={`mt-1 text-xs ${active ? "text-gray-500" : "text-white/80"}`}>
                Works out to $8.29/mo — pay for 10 months, get 12.
              </p>
            </>
          ) : (
            <>
              <p className="mt-4 text-3xl font-display">
                $9.95<span className="text-base opacity-70">/month</span>
              </p>
              <p className={`mt-1 text-xs ${active ? "text-gray-500" : "text-white/80"}`}>
                {active ? "Recurring monthly" : "First subscribers get a 3-day free trial."}
              </p>
            </>
          )}

          <ul className="mt-6 space-y-2 text-sm">
            <li className="flex items-center gap-2"><Check size={14}/> Read all customer reviews</li>
            <li className="flex items-center gap-2"><Check size={14}/> Post reviews on customers</li>
            <li className="flex items-center gap-2"><Check size={14}/> Browse the gated customer directory</li>
            <li className="flex items-center gap-2"><Check size={14}/> Manage anytime in Stripe portal</li>
          </ul>
          {!active && (
            <Button
              onClick={checkout}
              disabled={loading}
              className="mt-6 w-full rounded-[35px] bg-white text-[#B31E1E] hover:bg-gray-100"
              data-testid="subscription-checkout-button"
            >
              {loading
                ? "Redirecting..."
                : billingPeriod === "annual"
                  ? "Subscribe annually with Stripe →"
                  : "Subscribe with Stripe →"}
            </Button>
          )}
        </Card>
      </div>

      {/* Marketing add-on */}
      <div className="mt-12" id="marketing-addon">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F9E4E1] text-[#B31E1E] border border-[#F0C9C4] px-3 py-1 text-[11px] font-semibold tracking-wide">
              <Megaphone size={11} /> Optional add-on · Business Pro
            </span>
            <h2 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-[#1B1614]">
              Marketing upgrade
            </h2>
            <p className="mt-2 text-[15px] text-[#5C4E48] max-w-2xl">
              Turn Servrevu into a full-service local marketing channel — reach opted-in customers with campaigns, coupons, and behaviour-triggered promotions.
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-black tracking-tight text-[#1B1614]">
              +$35<span className="text-lg font-semibold text-[#8B7B73]">/mo</span>
            </div>
            <div className="text-xs text-[#8B7B73] mt-1">
              on top of Business Pro · Total <span className="font-semibold text-[#1B1614]">$34.95/mo</span>
            </div>
          </div>
        </div>

        <Card
          className={`rounded-2xl border p-8 bg-white ${
            marketing
              ? "border-[#B31E1E]/40 shadow-[0_10px_40px_-15px_rgba(179,30,30,0.35)]"
              : "border-[#EAE3DE] shadow-[0_4px_24px_-8px_rgba(179,30,30,0.10)]"
          }`}
          data-testid="marketing-card"
        >
          {/* Status badges */}
          <div className="flex items-center gap-2 flex-wrap mb-6">
            {marketing && (
              <Badge className="rounded-full bg-emerald-500 hover:bg-emerald-500 text-white border-0 font-mono text-[10px]">
                Active
              </Badge>
            )}
            {marketing && marketingCancelAtEnd && (
              <Badge className="rounded-full bg-amber-500 hover:bg-amber-500 text-white border-0 font-mono text-[10px]">
                Cancels at period end
              </Badge>
            )}
            {marketing && marketingPeriodEnd && (
              <span className="text-xs font-mono text-[#8B7B73]" data-testid="marketing-period-end">
                {marketingCancelAtEnd ? "Access until " : "Renews "}
                {new Date(marketingPeriodEnd).toLocaleDateString()}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
            {[
              { Icon: Target,     title: "Advanced audience targeting",       body: "Segment opted-in customers by category, city, ZIP code, favourites and last visit." },
              { Icon: Mail,       title: "Unlimited broadcast emails",         body: "Send campaigns with photos, buttons, coupon codes, and rich-text via the built-in email builder." },
              { Icon: Gift,       title: "Coupons & limited-time offers",      body: "Attach unique redeemable coupon codes with expiration dates and per-customer limits." },
              { Icon: Sparkles,   title: "Birthday & anniversary autopilot",   body: "Auto-send personalised birthday and re-engagement promos to loyal customers." },
              { Icon: Megaphone,  title: "Referral program tools",             body: "Reward customers who bring their friends with tracked referral links and bonus offers." },
              { Icon: BarChart3,  title: "Marketing analytics dashboard",      body: "Track opens, clicks, redemptions, and campaign ROI in one report card." },
            ].map((f) => (
              <div key={f.title} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#F9E4E1] flex items-center justify-center shrink-0">
                  <f.Icon size={16} className="text-[#B31E1E]" />
                </div>
                <div>
                  <div className="font-semibold text-[#1B1614]">{f.title}</div>
                  <div className="text-sm text-[#5C4E48] mt-0.5 leading-relaxed">{f.body}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-[#EAE3DE] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="text-sm text-[#5C4E48]">
              {!active ? (
                <span className="inline-flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                  <Check size={14} /> Subscribe to Business Pro first to enable Marketing.
                </span>
              ) : marketing && !marketingCancelAtEnd ? (
                <span>You have <strong>Marketing</strong> active. Cancel anytime and keep Pro.</span>
              ) : marketing && marketingCancelAtEnd ? (
                <span>Marketing will end at the current period close.</span>
              ) : (
                <span>Cancel the add-on anytime and keep Business Pro.</span>
              )}
            </div>
            <div>
              {active && !marketing && (
                <Button
                  onClick={marketingCheckout}
                  disabled={marketingLoading}
                  className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white h-11 px-5 font-semibold"
                  data-testid="marketing-checkout-button"
                >
                  {marketingLoading ? "Redirecting…" : "Add Marketing to Pro · $35/mo"}
                </Button>
              )}
              {active && marketing && !marketingCancelAtEnd && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="marketing-cancel-button"
                    >
                      Cancel Marketing
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-xl">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel Marketing add-on?</AlertDialogTitle>
                      <AlertDialogDescription>
                        You&apos;ll keep marketing access until{" "}
                        {marketingPeriodEnd ? new Date(marketingPeriodEnd).toLocaleDateString() : "the end of the current period"}.
                        Your Business Pro subscription is unaffected.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-lg">Keep Marketing</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={marketingCancel}
                        disabled={marketingCancelLoading}
                        className="rounded-lg bg-red-600 hover:bg-red-700"
                        data-testid="marketing-cancel-confirm"
                      >
                        {marketingCancelLoading ? "Cancelling…" : "Yes, cancel"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {active && marketing && marketingCancelAtEnd && (
                <Button
                  onClick={marketingResume}
                  disabled={marketingResumeLoading}
                  className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white h-11 px-5 font-semibold"
                  data-testid="marketing-resume-button"
                >
                  {marketingResumeLoading ? "Resuming…" : "Resume Marketing"}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Marketing Plus tier — drops promotion email cooldown from 7 to 3 days */}
      <div className="mt-6">
        <Card
          className={`rounded-md border-2 p-8 ${
            plus
              ? "border-fuchsia-500 bg-gradient-to-br from-fuchsia-50 to-white shadow-md"
              : "border-fuchsia-400/40 bg-white shadow-sm"
          }`}
          data-testid="marketing-plus-card"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="rounded-md bg-fuchsia-600 hover:bg-fuchsia-600 text-white border-0">
                  <Megaphone size={11} className="mr-1" /> Marketing Plus
                </Badge>
                {plus && (
                  <Badge variant="outline" className="rounded-md border-emerald-400 text-emerald-700">Active</Badge>
                )}
                {plus && plusCancelAtEnd && (
                  <Badge className="rounded-md bg-amber-500 hover:bg-amber-500">Cancels at period end</Badge>
                )}
              </div>
              <h3 className="font-display text-3xl mt-4">Message customers twice a week, not once.</h3>
              <p className="mt-2 text-gray-600 max-w-xl">
                Drops your promotional message cooldown from <strong>7 days → 3 days</strong>. Keep top-of-mind for
                seasonal pushes, flash sales, and weekly specials. Layers on top of your existing Marketing add-on.
              </p>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <li className="flex items-center gap-2"><Check size={14} className="text-fuchsia-600" /> ~2 promo messages/week (vs 1)</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-fuchsia-600" /> Overrides the global cooldown</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-fuchsia-600" /> Cancel anytime — reverts at period end</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-fuchsia-600" /> No setup — instant on checkout</li>
              </ul>
              {plus && plusPeriodEnd && (
                <p className="mt-4 text-xs font-mono text-gray-500" data-testid="marketing-plus-period-end">
                  {plusCancelAtEnd ? "Marketing Plus access until " : "Renews on "}
                  {new Date(plusPeriodEnd).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="text-right lg:text-left">
              <div className="font-display">
                <span className="text-4xl">+$10</span>
                <span className="text-base text-gray-500">/mo</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">on top of Marketing ($35/mo)</p>
              <div className="mt-5 flex flex-col gap-2">
                {!active && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Subscribe to servrevu Pro first.
                  </p>
                )}
                {active && !marketing && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Add the base Marketing tier first to unlock Plus.
                  </p>
                )}
                {active && marketing && !plus && (
                  <Button
                    onClick={plusCheckout}
                    disabled={plusLoading}
                    className="rounded-[35px] shadow-sm hover:shadow-md bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                    data-testid="marketing-plus-checkout-button"
                  >
                    {plusLoading ? "Redirecting…" : "Upgrade to Plus — $10/mo"}
                  </Button>
                )}
                {active && marketing && plus && !plusCancelAtEnd && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        className="rounded-[35px] shadow-sm hover:shadow-md text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="marketing-plus-cancel-button"
                      >
                        Cancel Marketing Plus
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-md">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel Marketing Plus?</AlertDialogTitle>
                        <AlertDialogDescription>
                          You'll keep Marketing Plus benefits until{" "}
                          {plusPeriodEnd ? new Date(plusPeriodEnd).toLocaleDateString() : "the end of the current period"}.
                          Your base Marketing and Pro subscriptions are unaffected.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-[35px]">Keep Plus</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={plusCancel}
                          disabled={plusCancelLoading}
                          className="rounded-[35px] bg-red-600 hover:bg-red-700"
                          data-testid="marketing-plus-cancel-confirm"
                        >
                          {plusCancelLoading ? "Cancelling…" : "Yes, cancel"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {active && marketing && plus && plusCancelAtEnd && (
                  <Button
                    onClick={plusResume}
                    disabled={plusResumeLoading}
                    className="rounded-[35px] shadow-sm hover:shadow-md bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                    data-testid="marketing-plus-resume-button"
                  >
                    {plusResumeLoading ? "Resuming…" : "Resume Marketing Plus"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Featured Listing add-on — only shown to existing subscribers so they can still cancel */}
      {featured && (
      <div className="mt-12">
        <div className="flex items-center gap-2 mb-4">
          <Star size={16} className="text-amber-500 fill-amber-400" />
          <span className="overline text-gray-500">Premium add-on</span>
        </div>
        <Card
          className={`rounded-md border-2 p-8 ${
            featured
              ? "border-amber-400 bg-gradient-to-br from-amber-50 to-white shadow-md"
              : "border-amber-300 bg-white shadow-sm"
          }`}
          data-testid="featured-card"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2">
                <Badge className="rounded-md bg-amber-500 hover:bg-amber-500 text-white border-0">
                  <Star size={11} className="mr-1 fill-white" /> Featured Listing
                </Badge>
                {featured && (
                  <Badge variant="outline" className="rounded-md border-emerald-400 text-emerald-700">
                    Active
                  </Badge>
                )}
                {featured && featuredCancelAtEnd && (
                  <Badge className="rounded-md bg-amber-500 hover:bg-amber-500">Cancels at period end</Badge>
                )}
              </div>
              <h3 className="font-display text-3xl mt-4">Stand out in the directory.</h3>
              <p className="mt-2 text-gray-600 max-w-xl">
                Get a premium accent border, a <strong>★ Featured</strong> badge on your card, and
                automatic top placement above non-featured competitors inside every category you list under.
              </p>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <li className="flex items-center gap-2"><Check size={14} className="text-amber-600" /> Always above non-featured in your category</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-amber-600" /> Gold accent border on directory cards</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-amber-600" /> ★ Featured badge on every listing</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-amber-600" /> Cancel anytime — no commitment</li>
              </ul>
              {featured && featuredPeriodEnd && (
                <p className="mt-4 text-xs font-mono text-gray-500" data-testid="featured-period-end">
                  {featuredCancelAtEnd ? "Featured access until " : "Renews on "}
                  {new Date(featuredPeriodEnd).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="text-right lg:text-left">
              <div className="font-display">
                <span className="text-4xl">+$5</span>
                <span className="text-base text-gray-500">/mo</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">on top of servrevu Pro</p>
              <div className="mt-5 flex flex-col gap-2">
                {!active && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Subscribe to servrevu Pro first to unlock the Featured add-on.
                  </p>
                )}
                {active && !featured && (
                  <Button
                    onClick={featuredCheckout}
                    disabled={featuredLoading}
                    className="rounded-[35px] shadow-sm hover:shadow-md bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="featured-checkout-button"
                  >
                    {featuredLoading ? "Redirecting…" : "Upgrade to Featured ★"}
                  </Button>
                )}
                {active && featured && !featuredCancelAtEnd && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        className="rounded-[35px] shadow-sm hover:shadow-md text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="featured-cancel-button"
                      >
                        Cancel Featured
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-md">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel Featured Listing?</AlertDialogTitle>
                        <AlertDialogDescription>
                          You'll keep your featured perks until{" "}
                          {featuredPeriodEnd ? new Date(featuredPeriodEnd).toLocaleDateString() : "the end of the current period"}.
                          Your base subscription is unaffected.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-md" data-testid="featured-cancel-dismiss">Keep Featured</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={featuredCancel}
                          disabled={featuredCancelLoading}
                          className="rounded-md bg-red-600 hover:bg-red-700"
                          data-testid="featured-cancel-confirm"
                        >
                          {featuredCancelLoading ? "Cancelling…" : "Yes, cancel"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {active && featured && featuredCancelAtEnd && (
                  <Button
                    onClick={featuredResume}
                    disabled={featuredResumeLoading}
                    className="rounded-[35px] shadow-sm hover:shadow-md bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="featured-resume-button"
                  >
                    {featuredResumeLoading ? "Resuming…" : "Resume Featured"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
      )}
    </div>
  );
}
